'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { InsufficientCoinsDetail } from '~/lib/auth-error';

interface OutOfCoinsContextType {
  isOpen: boolean;
  /** Coins the blocked action costs, when known. */
  neededCoins?: number;
  /** User's balance captured at the moment they ran out, when known. */
  currentBalance?: number;
  openOutOfCoins: (detail?: InsufficientCoinsDetail) => void;
  closeOutOfCoins: () => void;
}

const OutOfCoinsContext = createContext<OutOfCoinsContextType | null>(null);

/**
 * Drives the focused "out of coins" popup that appears when a user runs out of
 * coins mid-action (e.g. generating a chat image/video). Distinct from the full
 * BuyCoinsModal, which stays the proactive "browse all packs" experience.
 */
export function OutOfCoinsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    isOpen: boolean;
    detail: InsufficientCoinsDetail;
  }>({ isOpen: false, detail: {} });

  const openOutOfCoins = useCallback((detail?: InsufficientCoinsDetail) => {
    setState({ isOpen: true, detail: detail ?? {} });
  }, []);

  const closeOutOfCoins = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const value = useMemo<OutOfCoinsContextType>(
    () => ({
      isOpen: state.isOpen,
      neededCoins: state.detail.neededCoins,
      currentBalance: state.detail.currentBalance,
      openOutOfCoins,
      closeOutOfCoins,
    }),
    [state, openOutOfCoins, closeOutOfCoins],
  );

  return (
    <OutOfCoinsContext.Provider value={value}>
      {children}
    </OutOfCoinsContext.Provider>
  );
}

export function useOutOfCoins() {
  const context = useContext(OutOfCoinsContext);
  if (!context) {
    throw new Error('useOutOfCoins must be used within OutOfCoinsProvider');
  }
  return context;
}
